import React, { useState, useEffect } from 'react';
import { 
    COUNTRIES_WITH_STATES, 
    getFilteredCountries, 
    getStatesForCountry,
    getCitiesForLocation,
    transformCityName,
    getCountryName
} from '../../../utils/locationUtils';

function LocationSelector({ 
    countryCode, 
    stateCode,
    city, 
    onCountryChange, 
    onStateChange,
    onCityChange, 
    required = false,
    className = ''
}) {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCountries = async () => {
        try {
            const filteredCountries = getFilteredCountries();
            setCountries(filteredCountries);
            setLoading(false);
        } catch (err) {
            setError(err.message || 'Failed to get countries');
            setLoading(false);
        }
    };

    // Fetch countries
    useEffect(() => {
        fetchCountries();
    }, []);

    // Update states when country changes
    useEffect(() => {
        const countryStates = getStatesForCountry(countryCode);
        setStates(countryStates);
    }, [countryCode]);

    // Update cities when country or state changes
    useEffect(() => {
        if (countryCode) {
            const locationCities = getCitiesForLocation(countryCode, stateCode);
            if (locationCities.length === 0) {
                if (!COUNTRIES_WITH_STATES.includes(countryCode) || (COUNTRIES_WITH_STATES.includes(countryCode) && stateCode)) {
                    const currentCountryName = getCountryName(countryCode);
                    if (currentCountryName && onCityChange) {
                        onCityChange(currentCountryName);
                        setCities([{ name: currentCountryName }]);
                    } else {
                        setCities([]);
                    }
                } else {
                    setCities([]);
                    if (onCityChange) {
                        onCityChange('');
                    }
                }
            } else {
                setCities(locationCities);
            }
        } else {
            setCities([]);
            if (onCityChange) {
                onCityChange('');
            }
        }
    }, [countryCode, stateCode, onCityChange]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div className="error-message">{error}</div>;

    return (
        <div className={`location-select ${className}`} style={{ display: 'flex', gap: '10px' }}>
            <select
                name="country"
                value={countryCode || ''}
                onChange={(e) => {
                    onCountryChange(e.target.value);
                    if (onStateChange) onStateChange('');
                }}
                required={required}
                className="form-input"
                style={{ flex: 1 }}
            >
                <option value="">Select Country</option>
                {countries.map(country => (
                    <option key={country.isoCode} value={country.isoCode}>
                        {country.name}
                    </option>
                ))}
            </select>

            {COUNTRIES_WITH_STATES.includes(countryCode) && (
                <select
                    name="state"
                    value={stateCode || ''}
                    onChange={(e) => {
                        if (onStateChange) onStateChange(e.target.value);
                    }}
                    required={required}
                    className="form-input"
                    style={{ flex: 1 }}
                >
                    <option value="">Select State</option>
                    {states.map(state => (
                        <option key={state.isoCode} value={state.isoCode}>
                            {state.name}
                        </option>
                    ))}
                </select>
            )}

            <select
                name="city"
                value={city || ''}
                onChange={(e) => onCityChange(e.target.value)}
                required={required}
                disabled={!countryCode || (COUNTRIES_WITH_STATES.includes(countryCode) && !stateCode)}
                className="form-input"
                style={{ flex: 1 }}
            >
                <option value="">
                    {!countryCode 
                        ? 'Select country first' 
                        : COUNTRIES_WITH_STATES.includes(countryCode) && !stateCode 
                            ? 'Select state first'
                            : 'Select City'
                    }
                </option>
                {cities.map((city, index) => (
                    <option key={`${city.name}-${index}`} value={city.name}>
                        {transformCityName(city.name)}
                    </option>
                ))}
            </select>
        </div>
    );
}

export default LocationSelector; 